import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  createWalletTransaction,
  createWithdrawRequest,
} from "../../../../api/walletTransaction";
import { sendBNB } from "../../../../config/walletConfig";

const BalanceCard = ({ summary, refresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("buy"); // buy or sell
  const [quantity, setQuantity] = useState("");
  const [walletAddress, setWalletAddress] = useState(""); // 👈 NEW for withdraw
  const [clickedMax, setClickedMax] = useState(false);

  const handleOpenModal = (type) => {
    setModalType(type);
    setQuantity("");
    setWalletAddress(""); // reset
    setShowModal(true);
    setClickedMax(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quantity || isNaN(quantity) || quantity <= 0) {
      Swal.fire({
        icon: "error",
        title: "Invalid Quantity",
        text: "Please enter a valid quantity",
      });
      return;
    }

    if (modalType === "sell" && !walletAddress) {
      Swal.fire({
        icon: "error",
        title: "Missing Wallet Address",
        text: "Please enter the wallet address to withdraw funds.",
      });
      return;
    }

    try {
      let transactionHash = null;

      if (modalType === "buy") {
        transactionHash = await sendBNB(Number(quantity));

        if (!transactionHash) {
          Swal.fire({
            icon: "error",
            title: "Transaction Failed",
            text: "Could not get transaction hash. Please try again.",
          });
          return;
        }
      }

      if (modalType === "sell") {

          const status = await createWithdrawRequest({
            quantity: Number(quantity),
            walletAddress,
          });

          if (status === 201 || status === 200) {
            Swal.fire({
              icon: "success",
              title: "Withdrawal request submitted!",
              text: "Your request has been submitted for approval.",
              timer: 1800,
              showConfirmButton: false,
            });
            setShowModal(false);
            await refresh();
            return;
          }

          Swal.fire({
            icon: "error",
            title: "Request Failed",
            text: "Could not submit withdrawal request. Please try again.",
          });
          return;
      }

      const payload = {
        type: modalType,
        quantity: Number(quantity),
        walletTransactionId: transactionHash,
        walletAddress: modalType === "sell" ? walletAddress : null,
      };

      const status = await createWalletTransaction(payload);

      if (status === 201 || status === 200) {
        Swal.fire({
          icon: "success",
          title: `${modalType === "buy" ? "Deposit" : "Withdraw"} successful!`,
          timer: 1500,
          showConfirmButton: false,
        });
        setShowModal(false);
        await refresh();
      } else {
        throw new Error("Failed to submit transaction");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error.message || "Something went wrong",
      });
    }
  };

  return (
    <>
      {/* ... balance card UI unchanged ... */}
      <div className="col-xl-6 col-lg-6 col-sm-12 mb-4">
        <div
          className="card exchange d-flex flex-column"
          style={{ height: "98%" }}
        >
          <div className="card-header border-0 pb-0">
            <h2 className="heading">Your Balance</h2>
          </div>

          <div className="card-body pt-0 d-flex flex-column">
            <div className="balance mb-1">
              <div className="header-content">
                <h6 className="text-muted">$ {summary.profitLoss}</h6>
              </div>
              <div className="d-flex justify-content-between align-items-baseline">
                <h4 className="count-num">$ {summary?.totalBalance}</h4>
                <span className="fs-14 font-w400 text-success">
                  {/* <i className="bi bi-caret-up-fill pe-2"></i>+ 15% */}
                </span>
              </div>
            </div>
            <div className="mt-auto pt-3 d-flex flex-column flex-sm-row gap-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => handleOpenModal("buy")}
              >
                Deposit
              </button>
              <button
                disabled={summary?.totalBalance < 0}
                className="btn btn-secondary w-100"
                onClick={() => handleOpenModal("sell")}
              >
                Withdraw
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Modal for Deposit/Withdraw */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === "buy" ? "Deposit" : "Withdraw"} Funds
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group controlId="formQuantity" className="mb-3">
              <Form.Label>Amount</Form.Label>
              <div className="position-relative">
                <Form.Control
                  className="rounded"
                  style={modalType === "sell" ? { paddingRight: "3.2rem" } : {}}
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Enter amount"
                  value={quantity}
                  max={modalType === "sell" ? summary?.totalBalance : undefined}
                  onChange={(e) => {
                    setQuantity(e.target.value);
                    setClickedMax(false);
                  }}
                  required
                  autoFocus
                />
                {modalType === "sell" && (
                  <button
                    type="button"
                    className={`position-absolute top-50 end-0 translate-middle-y me-3 btn btn-md p-0 border-0 bg-transparent ${
                      clickedMax ? "text-secondary" : "text-muted"
                    }`}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setQuantity(Number(summary.totalBalance));
                      setClickedMax(true);
                    }}
                  >
                    Max
                  </button>
                )}
              </div>
            </Form.Group>

            {/* 👇 Extra input for withdrawal */}
            {modalType === "sell" && (
              <Form.Group controlId="formWalletAddress">
                <Form.Label>Wallet Address</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter recipient wallet address"
                  value={walletAddress}
                  onChange={(e) => setWalletAddress(e.target.value)}
                  required
                />
              </Form.Group>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="primary"
              disabled={
                quantity > summary?.totalBalance && modalType === "sell"
              }
              type="submit"
            >
              {modalType === "buy" ? "Deposit" : "Withdraw"}
            </Button>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default BalanceCard;
